import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { Country } from 'country-state-city';
import { productService } from '../../_services/product.service';
import '../new-service/ServiceEditorPanel.css'; // shared sep-* form fields + teal toggle
import './ProductV2.css';                         // pv2-* page surfaces, cards, buttons

// Online product store settings ("Store" tab). Publish/unpublish, store info,
// collection, and a configurable shipping engine (zones -> rate rules:
// flat / free-over / price-tier / weight-tier). Wires to /product/store.
// Surfaces use the pv2-* page language so this tab matches its siblings
// (Products / Data / Orders); fields reuse the app's shared sep-* form vocab.

const unwrap = (res) => (res && res.data ? (res.data.responseData !== undefined ? res.data.responseData : res.data) : {});

const RATE_TYPES = [
  { value: 'flat', label: 'Flat rate' },
  { value: 'free', label: 'Free over amount' },
  { value: 'price', label: 'By order value' },
  { value: 'weight', label: 'By weight' },
];

// Weight thresholds are stored canonically in grams (the engine compares cart
// weight in grams), but entered in g / kg / oz / lb — same convention as the
// product weight field. Unit is a display-only convenience (stripped on save).
const WEIGHT_UNITS = [
  { value: 'g', label: 'g', toG: 1 },
  { value: 'kg', label: 'kg', toG: 1000 },
  { value: 'oz', label: 'oz', toG: 28.3495 },
  { value: 'lb', label: 'lb', toG: 453.592 },
];
const unitToG = (unit) => (WEIGHT_UNITS.find((u) => u.value === unit) || WEIGHT_UNITS[0]).toG;
const weightToGrams = (val, unit) => {
  const n = parseFloat(val);
  if (!isFinite(n) || n <= 0) return 0;
  // Keep 2dp of gram precision so oz/lb survive the round-trip
  // (99oz → 2806.6g → 99oz, not 2807g → 99.014oz).
  return Math.round(n * unitToG(unit) * 100) / 100;
};
const gramsToDisplay = (grams, unit) => String(+((Number(grams) || 0) / unitToG(unit)).toFixed(3));
// Friendly default unit for an existing grams value (kg once it's 1kg+).
const defaultUnitForGrams = (grams) => (Number(grams) >= 1000 ? 'kg' : 'g');

// Country picker (Shopify-style): select from a list instead of typing ISO codes.
// '*' is the catch-all "rest of world" zone matched by the shipping engine.
const COUNTRY_OPTIONS = [
  { value: '*', label: '🌍  Rest of world (anywhere else)' },
  ...Country.getAllCountries().map((c) => ({ value: c.isoCode, label: `${c.flag}  ${c.name}` })),
];
const COUNTRY_BY_CODE = COUNTRY_OPTIONS.reduce((m, o) => { m[o.value] = o; return m; }, {});
const codesToOptions = (codes) => (codes || []).map((c) => COUNTRY_BY_CODE[c] || { value: c, label: c });

// Quick-add zone presets — fill a zone's countries in one click.
const EUROPE_CODES = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'NO', 'CH', 'IS'];
const ZONE_PRESETS = [
  { label: 'United Kingdom', name: 'United Kingdom', countries: ['GB'] },
  { label: 'Europe', name: 'Europe', countries: EUROPE_CODES },
  { label: 'Worldwide', name: 'Worldwide', countries: ['*'] },
];

// react-select styled to match the app's sep-input fields (teal focus, 10px radius).
const SELECT_STYLES = {
  control: (base, state) => ({
    ...base, minHeight: 44, borderRadius: 10, borderColor: state.isFocused ? '#007EA0' : '#e0e0e0',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(0, 126, 160, 0.08)' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#007EA0' : '#e0e0e0' }, fontSize: 14,
  }),
  valueContainer: (base) => ({ ...base, padding: '4px 10px' }),
  multiValue: (base) => ({ ...base, background: '#eef6f8', borderRadius: 6 }),
  multiValueLabel: (base) => ({ ...base, color: '#1A1A1E', fontSize: 13 }),
  multiValueRemove: (base) => ({ ...base, color: '#007EA0', ':hover': { background: '#d7ebf0', color: '#005f78' } }),
  placeholder: (base) => ({ ...base, color: '#aaa' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const Toggle = ({ checked, onChange }) => (
  <button type="button" className={`sep-toggle ${checked ? 'sep-toggle--on' : ''}`} onClick={() => onChange(!checked)} aria-pressed={checked}>
    <span className="sep-toggle-knob" />
  </button>
);

// App-standard bin icon (matches the "Delete automation" trash icon used elsewhere).
const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const ProductStoreSettings = ({ currency = '£' }) => {
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);     // publish/unpublish in flight
  const [msg, setMsg] = useState(null);            // publish/unpublish result
  const [status, setStatus] = useState('idle');    // auto-save: idle | saving | saved | error
  const savedTimer = useRef(null);

  useEffect(() => {
    let alive = true;
    productService.getStore().then((r) => { if (alive) { setStore(unwrap(r)); setLoading(false); } }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => () => clearTimeout(savedTimer.current), []);

  if (loading || !store) return <div style={{ padding: 24, color: '#8a8a90' }}>Loading store settings…</div>;

  const shipping = store.shipping || { enabled: false, zones: [] };
  const zones = shipping.zones || [];

  // Strip the raw input strings; the bounds stay canonical grams. weightUnit is
  // PERSISTED so the rate redisplays in the unit the salon entered it in.
  const cleanZones = (zs) => (zs || []).map((z) => ({
    ...z,
    rates: (z.rates || []).map(({ _minW, _maxW, ...rate }) => rate),
  }));

  // Auto-save (no Save button) — matches the app's settings pages. Persists the
  // given snapshot; local state stays authoritative so in-flight edits aren't clobbered.
  const persist = async (src = store) => {
    setStatus('saving');
    try {
      await productService.updateStore({
        storeInfo: src.storeInfo || {},
        pickup: { enabled: !!(src.pickup && src.pickup.enabled), instructions: (src.pickup && src.pickup.instructions) || '' },
        shipping: { enabled: !!(src.shipping && src.shipping.enabled), zones: cleanZones((src.shipping && src.shipping.zones) || []) },
        returnsPolicy: src.returnsPolicy || '',
        otherInfo: src.otherInfo || '',
      });
      setStatus('saved');
      clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setStatus('idle'), 2000);
    } catch (e) { setStatus('error'); }
  };

  // Text/number fields: update local state only; persist on blur (saveNow).
  const setInfo = (patch) => setStore((s) => ({ ...s, storeInfo: { ...(s.storeInfo || {}), ...patch } }));
  const setPickup = (patch) => setStore((s) => ({ ...s, pickup: { ...(s.pickup || {}), ...patch } }));
  const setShipping = (patch) => setStore((s) => ({ ...s, shipping: { ...(s.shipping || {}), ...patch } }));
  const setZone = (zi, patch) => setShipping({ zones: zones.map((z, i) => (i === zi ? { ...z, ...patch } : z)) });
  const setRate = (zi, ri, patch) => setZone(zi, { rates: (zones[zi].rates || []).map((r, i) => (i === ri ? { ...r, ...patch } : r)) });
  const saveNow = () => persist();

  // Toggles / selects / structural changes: build the next snapshot, set it, and save immediately.
  const commitStore = (next) => { setStore(next); persist(next); };
  const commitZones = (zonesNext) => commitStore({ ...store, shipping: { ...(store.shipping || {}), zones: zonesNext } });
  const toggleCollection = (v) => commitStore({ ...store, pickup: { ...(store.pickup || {}), enabled: v } });
  const toggleDelivery = (v) => commitStore({ ...store, shipping: { ...(store.shipping || {}), enabled: v } });
  const addZone = () => commitZones([...zones, { name: '', countries: [], rates: [{ name: 'Standard delivery', type: 'flat', price: 0 }] }]);
  const addZonePreset = (p) => commitZones([...zones, { name: p.name, countries: p.countries, rates: [{ name: 'Standard delivery', type: 'flat', price: 0 }] }]);
  const setZoneCountries = (zi, codes) => commitZones(zones.map((z, i) => (i === zi ? { ...z, countries: codes } : z)));
  const removeZone = (zi) => commitZones(zones.filter((_, i) => i !== zi));
  const addRate = (zi) => commitZones(zones.map((z, i) => (i === zi ? { ...z, rates: [...(z.rates || []), { name: '', type: 'flat', price: 0 }] } : z)));
  const removeRate = (zi, ri) => commitZones(zones.map((z, i) => (i === zi ? { ...z, rates: (z.rates || []).filter((_, j) => j !== ri) } : z)));
  const changeRateType = (zi, ri, type) => commitZones(zones.map((z, i) => (i === zi ? { ...z, rates: (z.rates || []).map((r, j) => (j === ri ? { ...r, type } : r)) } : z)));

  // Weight thresholds: keep the raw typed string for the input (no conversion jank)
  // and the canonical grams in sync. Bound is null when blank (= no limit / no floor).
  const onWeightInput = (zi, ri, field, val) => {
    const r = zones[zi].rates[ri];
    const unit = r.weightUnit || defaultUnitForGrams(r.maxWeight != null ? r.maxWeight : r.minWeight);
    const grams = val === '' ? (field === 'max' ? null : 0) : weightToGrams(val, unit);
    setRate(zi, ri, field === 'min' ? { _minW: val, minWeight: grams } : { _maxW: val, maxWeight: grams });
  };
  // Unit change keeps the physical weight constant (re-expresses the same grams).
  const changeRateWeightUnit = (zi, ri, unit) => commitZones(zones.map((z, i) => (i === zi ? {
    ...z,
    rates: (z.rates || []).map((r, j) => (j === ri ? {
      ...r, weightUnit: unit,
      _minW: r.minWeight != null && r.minWeight !== '' ? gramsToDisplay(r.minWeight, unit) : '',
      _maxW: r.maxWeight != null && r.maxWeight !== '' ? gramsToDisplay(r.maxWeight, unit) : '',
    } : r)),
  } : z)));

  const togglePublish = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = store.published ? await productService.unpublishStore() : await productService.publishStore();
      const pub = unwrap(r);
      // Merge only the published flag so unsaved field edits aren't overwritten.
      setStore((s) => ({ ...s, published: pub.published }));
      setMsg({ type: 'ok', text: store.published ? 'Store unpublished' : 'Store published, it’s now live' });
    } catch (e) {
      const t = e && e.response && e.response.data && (e.response.data.messages || (e.response.data.error && e.response.data.error.responseMessage));
      setMsg({ type: 'err', text: typeof t === 'string' ? t : 'Could not change publish state' });
    }
    setSaving(false);
  };

  const numInput = (val, onChange, ph) => (
    <input className="sep-input" type="number" min="0" step="0.01" value={val != null && val !== undefined ? val : ''} onChange={onChange} onBlur={saveNow} placeholder={ph} />
  );

  return (
    <div className="pv2-store">
      {/* Status */}
      <div className="pv2-card">
        <div className="pv2-store-row">
          <div className="pv2-store-row-info">
            <div className="pv2-store-row-title">
              Online store {store.published ? <span className="pv2-store-live">· Live</span> : '· Offline'}
            </div>
            <div className="pv2-store-row-desc">
              {store.published ? 'Clients can buy your products online.' : 'Enable collection or delivery, then publish to go live.'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {msg && <span className={msg.type === 'ok' ? 'pv2-store-msg--ok' : 'pv2-store-msg--err'}>{msg.text}</span>}
            <button type="button" className={`pv2-btn ${store.published ? 'pv2-btn--ghost' : 'pv2-btn--primary'}`} disabled={saving} onClick={togglePublish}>
              {store.published ? 'Unpublish' : 'Publish store'}
            </button>
          </div>
        </div>
      </div>

      {/* Store details */}
      <div className="pv2-card">
        <p className="pv2-card-title">Store details</p>
        <div className="sep-field">
          <label className="sep-field-label">Store name</label>
          <input className="sep-input" value={(store.storeInfo && store.storeInfo.name) || ''} onChange={(e) => setInfo({ name: e.target.value })} onBlur={saveNow} placeholder="e.g. The Salon Shop" />
        </div>
        <div className="sep-field">
          <label className="sep-field-label">Description</label>
          <textarea className="sep-input sep-textarea" value={(store.storeInfo && store.storeInfo.description) || ''} onChange={(e) => setInfo({ description: e.target.value })} onBlur={saveNow} placeholder="A short line about your products" />
        </div>
      </div>

      {/* Collection */}
      <div className="pv2-card">
        <div className="pv2-store-row">
          <div className="pv2-store-row-info">
            <div className="pv2-store-row-title">Collection</div>
            <div className="pv2-store-row-desc">Let clients collect from the salon.</div>
          </div>
          <Toggle checked={!!(store.pickup && store.pickup.enabled)} onChange={toggleCollection} />
        </div>
        {store.pickup && store.pickup.enabled && (
          <div className="sep-field" style={{ marginTop: 16 }}>
            <label className="sep-field-label">Collection instructions (optional)</label>
            <input className="sep-input" value={(store.pickup && store.pickup.instructions) || ''} onChange={(e) => setPickup({ instructions: e.target.value })} onBlur={saveNow} placeholder="e.g. Collect from reception, Mon to Sat, 9 to 5" />
          </div>
        )}
      </div>

      {/* Delivery */}
      <div className="pv2-card">
        <div className="pv2-store-row">
          <div className="pv2-store-row-info">
            <div className="pv2-store-row-title">Delivery</div>
            <div className="pv2-store-row-desc">Ship to clients. Set up zones, then add rates: flat, free over an amount, by order value, or by weight.</div>
          </div>
          <Toggle checked={!!shipping.enabled} onChange={toggleDelivery} />
        </div>

        {shipping.enabled && (
          <div style={{ marginTop: 16 }}>
            {zones.map((z, zi) => (
              <div className="pv2-store-zone" key={zi}>
                <div className="sep-field-row" style={{ alignItems: 'flex-end' }}>
                  <div className="sep-field" style={{ flex: 2 }}>
                    <label className="sep-field-label">Zone name</label>
                    <input className="sep-input" value={z.name || ''} onChange={(e) => setZone(zi, { name: e.target.value })} onBlur={saveNow} placeholder="United Kingdom" />
                  </div>
                  <div className="sep-field" style={{ flex: 3 }}>
                    <label className="sep-field-label">Countries</label>
                    <Select
                      isMulti
                      options={COUNTRY_OPTIONS}
                      value={codesToOptions(z.countries)}
                      onChange={(vals) => setZoneCountries(zi, (vals || []).map((v) => v.value))}
                      styles={SELECT_STYLES}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      placeholder="Search and add countries…"
                      classNamePrefix="pv2-country-select"
                    />
                  </div>
                  <button type="button" className="pv2-iconbtn" onClick={() => removeZone(zi)} title="Remove zone" aria-label="Remove zone"><TrashIcon /></button>
                </div>

                <div style={{ marginTop: 10 }}>
                  {(z.rates || []).map((r, ri) => (
                    <div className="pv2-store-rate" key={ri}>
                      <div className="sep-field-row" style={{ alignItems: 'flex-end' }}>
                        <div className="sep-field" style={{ flex: 2 }}>
                          <label className="sep-field-label">Rate name</label>
                          <input className="sep-input" value={r.name || ''} onChange={(e) => setRate(zi, ri, { name: e.target.value })} onBlur={saveNow} placeholder="Standard (2 to 3 days)" />
                        </div>
                        <div className="sep-field" style={{ flex: 1 }}>
                          <label className="sep-field-label">Type</label>
                          <select className="sep-input" value={r.type || 'flat'} onChange={(e) => changeRateType(zi, ri, e.target.value)}>
                            {RATE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                        {r.type !== 'free' && (
                          <div className="sep-field" style={{ flex: 1 }}>
                            <label className="sep-field-label">Fee ({currency})</label>
                            {numInput(r.price, (e) => setRate(zi, ri, { price: e.target.value }), '0.00')}
                          </div>
                        )}
                        <button type="button" className="pv2-iconbtn" onClick={() => removeRate(zi, ri)} title="Remove rate" aria-label="Remove rate"><TrashIcon /></button>
                      </div>

                      {/* Condition fields by type */}
                      {r.type === 'free' && (
                        <div className="sep-field" style={{ marginTop: 6, maxWidth: 220 }}>
                          <label className="sep-field-label">Free when order is over ({currency})</label>
                          {numInput(r.minSubtotal, (e) => setRate(zi, ri, { minSubtotal: e.target.value, price: 0 }), '50.00')}
                        </div>
                      )}
                      {r.type === 'price' && (
                        <div className="sep-field-row" style={{ marginTop: 6 }}>
                          <div className="sep-field"><label className="sep-field-label">Order from ({currency})</label>{numInput(r.minSubtotal, (e) => setRate(zi, ri, { minSubtotal: e.target.value }), '0.00')}</div>
                          <div className="sep-field"><label className="sep-field-label">Order up to ({currency})</label>{numInput(r.maxSubtotal, (e) => setRate(zi, ri, { maxSubtotal: e.target.value }), 'no limit')}</div>
                        </div>
                      )}
                      {r.type === 'weight' && (() => {
                        const wUnit = r.weightUnit || defaultUnitForGrams(r.maxWeight != null ? r.maxWeight : r.minWeight);
                        const minDisp = r._minW !== undefined ? r._minW : (r.minWeight != null && r.minWeight !== '' ? gramsToDisplay(r.minWeight, wUnit) : '');
                        const maxDisp = r._maxW !== undefined ? r._maxW : (r.maxWeight != null && r.maxWeight !== '' ? gramsToDisplay(r.maxWeight, wUnit) : '');
                        return (
                          <div className="sep-field-row" style={{ marginTop: 6, alignItems: 'flex-end' }}>
                            <div className="sep-field">
                              <label className="sep-field-label">Weight from</label>
                              <input className="sep-input" type="number" min="0" step="any" value={minDisp} onChange={(e) => onWeightInput(zi, ri, 'min', e.target.value)} onBlur={saveNow} placeholder="0" />
                            </div>
                            <div className="sep-field">
                              <label className="sep-field-label">Weight up to</label>
                              <input className="sep-input" type="number" min="0" step="any" value={maxDisp} onChange={(e) => onWeightInput(zi, ri, 'max', e.target.value)} onBlur={saveNow} placeholder="no limit" />
                            </div>
                            <div className="sep-field" style={{ flex: '0 0 84px' }}>
                              <label className="sep-field-label">Unit</label>
                              <select className="sep-input" value={wUnit} onChange={(e) => changeRateWeightUnit(zi, ri, e.target.value)} aria-label="Weight unit">
                                {WEIGHT_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                              </select>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                  <button type="button" className="pv2-btn pv2-btn--ghost" onClick={() => addRate(zi)}>+ Add rate</button>
                  {(() => {
                    // Coverage check: if EVERY rate in this zone has bounds, some
                    // baskets will match nothing and the customer can't order
                    // delivery (the classic gapped-weight-bands trap). Warn here,
                    // at setup time — not via a lost sale.
                    const rs = z.rates || [];
                    if (!rs.length) return null;
                    const hasCatchAll = rs.some((r) =>
                      (r.minWeight == null || r.minWeight === '' || Number(r.minWeight) === 0) &&
                      (r.maxWeight == null || r.maxWeight === '') &&
                      (r.minSubtotal == null || r.minSubtotal === '' || Number(r.minSubtotal) === 0) &&
                      (r.maxSubtotal == null || r.maxSubtotal === ''));
                    if (hasCatchAll) return null;
                    return (
                      <p className="pv2-store-zone-warning">
                        Some orders won't match any of these rates (e.g. heavier or larger baskets) and
                        can't choose delivery. Add a rate with no limits as a catch-all, or leave your
                        biggest band's upper limit blank.
                      </p>
                    );
                  })()}
                </div>
              </div>
            ))}
            <div className="pv2-store-presets">
              <span className="pv2-store-presets-label">Add a zone:</span>
              {ZONE_PRESETS.map((p) => (
                <button type="button" className="pv2-store-chip" key={p.label} onClick={() => addZonePreset(p)}>{p.label}</button>
              ))}
              <button type="button" className="pv2-store-chip" onClick={addZone}>Custom</button>
            </div>
          </div>
        )}
      </div>

      {/* Auto-save status (no Save button — changes persist on edit, like the rest of the app) */}
      <div className={`pv2-store-savestate ${status === 'error' ? 'pv2-store-savestate--error' : ''}`}>
        {status === 'saving' ? 'Saving…' : status === 'saved' ? 'All changes saved' : status === 'error' ? 'Could not save, please try again' : ''}
      </div>
    </div>
  );
};

export default ProductStoreSettings;
